import { motion } from 'motion/react';

import { useTranslation } from '../i18n/hooks';

const featureIcons = [
  '/images/icons/video-camera.svg',
  '/images/icons/check-file.svg',
  '/images/icons/microscope.svg',
  '/images/icons/globe.svg',
  '/images/icons/shield.svg',
];

export default function WhyChooseUsSection() {
  const t = useTranslation();
  return (
    <motion.section
      id="why-choose-us"
      className="relative overflow-x-hidden bg-white px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      {/* Background Circle */}
      <motion.div
        className="blur-effect absolute top-4 right-[-50px] h-[250px] w-[250px] rounded-full bg-green-100 sm:right-[50px] sm:h-[350px] sm:w-[350px] md:right-[150px] md:h-[450px] md:w-[450px] lg:right-[300px] lg:h-[550px] lg:w-[550px] xl:right-[400px] xl:h-[640px] xl:w-[640px]"
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, duration: 1.2, ease: 'easeOut' }}
        viewport={{ once: true }}
      ></motion.div>

      <div className="relative z-10">
        <motion.div
          className="mb-10 text-center sm:mb-10"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="mx-auto mb-4 max-w-xs px-2 text-2xl font-bold text-gray-900 sm:max-w-lg sm:text-3xl md:max-w-2xl md:text-3xl lg:text-[32px]"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t.whyUs.title}
          </motion.h2>
          <motion.p
            className="mx-auto max-w-full px-2 text-base text-gray-900 sm:max-w-2xl sm:text-lg md:max-w-4xl md:text-xl"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t.whyUs.subtitle}
          </motion.p>
        </motion.div>

        {/* All Cards in Single Grid */}
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {t.whyUs.features.map((feature, index) => (
            <motion.div
              key={index}
              className={`card-shadow flex h-40 flex-col rounded-xl bg-white p-6 ${
                index < 3 ? 'lg:col-span-2' : 'lg:col-span-2'
              } ${index === 3 ? 'lg:col-start-2' : ''}`}
              initial={{
                x:
                  index === 0 || index === 3
                    ? -50
                    : index === 2 || index === 4
                      ? 50
                      : 0,
                y: index === 1 ? -50 : 0,
                opacity: 0,
              }}
              whileInView={{ x: 0, y: 0, opacity: 1 }}
              transition={{
                delay: 0.2 + index * 0.1,
                duration: 0.8,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
              whileHover={{
                y: -10,
                scale: 1.02,
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div className="mb-2 flex flex-1 items-start justify-between gap-2">
                <motion.div
                  className="h-5 w-5 shrink-0"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{
                    delay: 0.5 + index * 0.1,
                    duration: 0.3,
                    type: 'spring',
                  }}
                  viewport={{ once: true }}
                >
                  <img
                    src={featureIcons[index]}
                    alt={feature.title}
                    // width={28}
                    // height={28}
                    className="h-7 w-7"
                  />
                </motion.div>
                <motion.div
                  className="flex-1"
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h3 className="mb-2 text-lg font-bold text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-900">
                    {feature.description}
                  </p>
                </motion.div>
              </div>
              {/* Star Rating */}
              <div className="mt-auto mr-6 flex justify-start gap-0">
                {[...Array(5)].map((_, starIndex) => (
                  <img
                    key={starIndex}
                    src="/images/icons/star.svg"
                    alt="star"
                    // width={24}
                    // height={24}
                    className="h-6 w-6"
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
